import { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUsers } from '../store/UserSlice';
import { ProfileDetails } from '../store/ProfileSlice';
import { RootState, AppDispatch } from '../store/store';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';

const DropdownUser = ({ isDisabled = false }: { isDisabled?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const trigger = useRef<any>(null);
  const dropdown = useRef<any>(null);

  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const reduxProfile: any = useSelector((state: RootState) => state.profile.data);
  const reduxUser: any = useSelector((state: RootState) => state.user.user);

  // Fetch latest profile details on mount if user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(ProfileDetails());
    }
  }, [dispatch, isLoggedIn]);

  const handleImageError = () => {
    setImgError(true);
    if (isLoggedIn) {
      // Automatically request a fresh presigned URL from the server
      dispatch(ProfileDetails());
    }
  };

  const currentUser = useMemo(() => {
    let localLoginUser: any = null;
    let localProfile: any = null;
    try {
      localLoginUser = JSON.parse(localStorage.getItem('loginData') || '')?.user;
    } catch {
      localLoginUser = null;
    }
    try {
      localProfile = JSON.parse(localStorage.getItem('profile') || '');
    } catch {
      localProfile = null;
    }

    const reduxUserObj = reduxUser?.user || reduxUser;
    const reduxProfileObj = reduxProfile && !Array.isArray(reduxProfile) ? reduxProfile : null;

    return {
      ...(localLoginUser || {}),
      ...(localProfile || {}),
      ...(reduxUserObj && typeof reduxUserObj === 'object' ? reduxUserObj : {}),
      ...(reduxProfileObj || {}),
    };
  }, [reduxProfile, reduxUser]);

  const userName = currentUser?.fullName || currentUser?.name || "User";
  const userAvatar = currentUser?.icon || "";
  const isAdmin = currentUser?.isAdmin === true;
  const isCurrentlyAdminRoute = location.pathname.startsWith('/admin');

  // Reset img error if avatar URL updates
  useEffect(() => {
    setImgError(false);
  }, [userAvatar]);

  const handleLogout = () => {
    setDropdownOpen(false);
    dispatch(logoutUsers()).then(() => {
      toast.success(t("userSidebar.logoutSuccess", "Logged out Successfully."));
      if (isAdmin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    });
  };

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="relative">
      <Link
        ref={trigger}
        onClick={() => isDisabled ? null : setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-3 md:gap-4"
        to="#"
      >
        <span className="text-right hidden sm:block">
          <span className="block text-sm md:text-base font-semibold text-[#000024] leading-snug">
            {userName}
          </span>
        </span>

        <span className="h-10 w-10 md:h-12 md:w-12 rounded-full overflow-hidden shrink-0 border border-gray-200 bg-gray-100 flex items-center justify-center shadow-xs">
          {userAvatar && !imgError ? (
            <img
              src={userAvatar}
              alt="User"
              className="h-full w-full object-cover"
              onError={handleImageError}
            />
          ) : (
            <img
              src="/images/user/default-avatar.jpg"
              alt="User"
              className="h-full w-full object-cover"
            />
          )}
        </span>
        <svg
          className={`fill-current text-gray-600 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.410765 0.910734C0.736202 0.585297 1.26384 0.585297 1.58928 0.910734L6.00002 5.32148L10.4108 0.910734C10.7362 0.585297 11.2638 0.585297 11.5893 0.910734C11.9147 1.23617 11.9147 1.76381 11.5893 2.08924L6.58928 7.08924C6.26384 7.41468 5.7362 7.41468 5.41077 7.08924L0.410765 2.08924C0.0853277 1.76381 0.0853277 1.23617 0.410765 0.910734Z"
            fill=""
          />
        </svg>
      </Link>

      {/* <!-- Dropdown Start --> */}
      <div
        ref={dropdown}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setDropdownOpen(false)}
        className={`absolute right-0 mt-3 flex w-52 flex-col rounded-lg bg-[#1e293b] shadow-2xl py-2 z-50 border border-slate-700/60 ${
          dropdownOpen === true ? 'block' : 'hidden'
        }`}
      >
        {isAdmin && (
          isCurrentlyAdminRoute ? (
            <Link
              to="/"
              onClick={() => setDropdownOpen(false)}
              className="flex items-center text-white gap-3 py-2.5 px-5 text-sm duration-200 ease-in-out hover:text-[#037897] hover:bg-slate-800 font-semibold border-b border-slate-700"
            >
              <svg className="w-5 h-5 shrink-0 text-[#037897]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {t("nav.userView", "View Website")}
            </Link>
          ) : (
            <Link
              to="/admin/dashboard"
              onClick={() => setDropdownOpen(false)}
              className="flex items-center text-[#B4804A] gap-3 py-2.5 px-5 text-sm duration-200 ease-in-out hover:text-white hover:bg-[#B4804A]/30 font-semibold border-b border-slate-700"
            >
              <svg className="w-5 h-5 shrink-0 text-[#B4804A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t("nav.adminPanel", "Admin Panel")}
            </Link>
          )
        )}

        <Link
          to="/profile"
          onClick={() => setDropdownOpen(false)}
          className="flex items-center text-white gap-3 py-2.5 px-5 text-sm duration-200 ease-in-out hover:text-primary hover:bg-slate-800 font-medium"
        >
          <svg className="w-5 h-5 shrink-0 text-gray-300" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8.57897" cy="5.77803" r="4.77803" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path fillRule="evenodd" clipRule="evenodd" d="M1.00002 17.2013C0.998732 16.8654 1.07385 16.5336 1.2197 16.2311C1.67736 15.3157 2.96798 14.8306 4.03892 14.6109C4.81128 14.4461 5.59431 14.336 6.38217 14.2814C7.84084 14.1533 9.30793 14.1533 10.7666 14.2814C11.5544 14.3366 12.3374 14.4467 13.1099 14.6109C14.1808 14.8306 15.4714 15.27 15.9291 16.2311C16.2224 16.8479 16.2224 17.5639 15.9291 18.1807C15.4714 19.1418 14.1808 19.5812 13.1099 19.7917C12.3384 19.9633 11.5551 20.0766 10.7666 20.1304C9.57937 20.231 8.38659 20.2494 7.19681 20.1853C6.92221 20.1853 6.65677 20.1853 6.38217 20.1304C5.59663 20.0772 4.81632 19.964 4.04807 19.7917C2.96798 19.5812 1.68652 19.1418 1.2197 18.1807C1.0746 17.8746 0.999552 17.54 1.00002 17.2013Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t("nav.myAccount")}
        </Link>

        <button
          className="flex items-center text-white gap-3 py-2.5 px-5 text-sm duration-200 ease-in-out hover:text-red-400 hover:bg-slate-800 font-medium w-full text-left"
          onClick={handleLogout}
        >
          <svg
            className="w-5 h-5 shrink-0 text-gray-300"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.5375 0.618744H11.6531C10.7594 0.618744 10.0031 1.37499 10.0031 2.26874V4.64062C10.0031 5.05312 10.3469 5.39687 10.7594 5.39687C11.1719 5.39687 11.55 5.05312 11.55 4.64062V2.23437C11.55 2.16562 11.5844 2.13124 11.6531 2.13124H15.5375C16.3625 2.13124 17.0156 2.78437 17.0156 3.60937V18.3562C17.0156 19.1812 16.3625 19.8344 15.5375 19.8344H11.6531C11.5844 19.8344 11.55 19.8 11.55 19.7312V17.3594C11.55 16.9469 11.2062 16.6031 10.7594 16.6031C10.3125 16.6031 10.0031 16.9469 10.0031 17.3594V19.7312C10.0031 20.625 10.7594 21.3812 11.6531 21.3812H15.5375C17.2219 21.3812 18.5625 20.0062 18.5625 18.3562V3.64374C18.5625 1.95937 17.1875 0.618744 15.5375 0.618744Z"
              fill="currentColor"
            />
            <path
              d="M6.05001 11.7563H12.2031C12.6156 11.7563 12.9594 11.4125 12.9594 11C12.9594 10.5875 12.6156 10.2438 12.2031 10.2438H6.08439L8.21564 8.07813C8.52501 7.76875 8.52501 7.2875 8.21564 6.97812C7.90626 6.66875 7.42501 6.66875 7.11564 6.97812L3.67814 10.4844C3.36876 10.7938 3.36876 11.275 3.67814 11.5844L7.11564 15.0906C7.25314 15.2281 7.45939 15.3312 7.66564 15.3312C7.87189 15.3312 8.04376 15.2625 8.21564 15.125C8.52501 14.8156 8.52501 14.3344 8.21564 14.025L6.05001 11.7563Z"
              fill="currentColor"
            />
          </svg>
          {t("nav.logout")}
        </button>
      </div>
      {/* <!-- Dropdown End --> */}
    </div>
  );
};

export default DropdownUser;
